import { SearchOutlined } from "@ant-design/icons";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { Button, Form, Input, Space, Table, Tag } from "antd";
import axios from "axios";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { BiCheckCircle, BiCircle, BiXCircle } from "react-icons/bi";
import { useQuery } from "react-query";
import { serverUrl } from "../../../../../nestserver";

const { RangePicker } = DatePicker;

const AttendanceViewHistory = ({ classId: propClassId }) => {
  const [form] = Form.useForm();
  const searchInputRef = useRef(null);
  const [selectedClassId, setSelectedClassId] = useState(propClassId || null);
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days"),
    moment(),
  ]);

  useEffect(() => {
    if (propClassId) {
      setSelectedClassId(propClassId);
    }
  }, [propClassId]);

  function strcmp(a, b) {
    if (a === b) return 0;
    if (a > b) return 1;
    return -1;
  }

  const fetchAttendanceHistory = async () => {
    if (!selectedClassId || !dateRange[0] || !dateRange[1]) {
      return [];
    }

    const fromDate = dateRange[0].format("YYYY-MM-DD");
    const toDate = dateRange[1].format("YYYY-MM-DD");

    try {
      const dates = [];
      let currentDate = moment(fromDate);
      const endDate = moment(toDate);

      while (currentDate.isSameOrBefore(endDate)) {
        dates.push(currentDate.format("YYYY-MM-DD"));
        currentDate.add(1, "day");
      }

      const attendancePromises = dates.map((date) =>
        axios
          .get(
            `${serverUrl}/montessori/attendance/class/${selectedClassId}?date=${date}`,
            {
              headers: {
                Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
              },
            },
          )
          .catch((err) => {
            return { data: { result: { data: [] } } };
          }),
      );

      const attendanceResponses = await Promise.all(attendancePromises);

      const studentMap = {};
      const holidayMap = {};

      attendanceResponses.forEach((response, index) => {
        const date = dates[index];
        const resData = response.data?.result;
        const records = Array.isArray(resData?.data)
          ? resData.data
          : Array.isArray(resData)
            ? resData
            : [];

        if (resData?.holiday?.isHoliday) {
          holidayMap[date] = resData.holiday.name || "Holiday";
        }

        records.forEach((record) => {
          const studentId = record?.student?._id;
          if (!studentId) return;

          if (!studentMap[studentId]) {
            studentMap[studentId] = {
              _id: studentId,
              first_name: record.student.first_name,
              last_name: record.student.last_name,
              attendance: [],
            };
          }

          studentMap[studentId].attendance.push({
            date: date,
            is_checked_in: record.is_checked_in,
            is_checked_out: record.is_checked_out,
            permission: record.permission,
          });
        });
      });

      const results = Object.values(studentMap).map((student) => {
        return {
          ...student,
        };
      });

      return { students: results, holidayMap };
    } catch (error) {
      console.error("Fetch history error:", error);
      return { students: [], holidayMap: {} };
    }
  };

  const historyData = useQuery(
    ["attendance-history", selectedClassId, dateRange],
    fetchAttendanceHistory,
    {
      enabled: !!selectedClassId && !!dateRange[0] && !!dateRange[1],
    },
  );

  const attendanceMap = useMemo(() => {
    const students = historyData?.data?.students || [];
    return students.reduce((map, student) => {
      const studentMap = {};
      student.attendance.forEach((att) => {
        const date = moment(att.date).format("DD-MM-YYYY");
        studentMap[date] = {
          hasCheckIn: !!att.is_checked_in,
          hasCheckOut: !!att.is_checked_out,
          hasPermission: att.permission?.hasPermission,
        };
      });
      map[student._id] = studentMap;
      return map;
    }, {});
  }, [historyData?.data]);

  const handleExport = async () => {
    if (selectedClassId && dateRange[0] && dateRange[1]) {
      try {
        const response = await axios({
          url: `${serverUrl}/montessori/attendance/class/${selectedClassId}/export?fromDate=${dateRange[0].format(
            "YYYY-MM-DD",
          )}&toDate=${dateRange[1].format("YYYY-MM-DD")}`,
          method: "GET",
          responseType: "blob",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        saveAs(response.data, "montessori-attendance-history.xlsx");
      } catch (error) {
        console.error("Export failed:", error);
      }
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={
              <SearchOutlined
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            }
            size="small"
            style={{
              width: 90,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.select(), 100);
      }
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const columns = [
    {
      title: "First Name",
      dataIndex: "first_name",
      width: 150,
      fixed: "left",
      sorter: {
        compare: (a, b) => strcmp(a.first_name, b.first_name),
        multiple: 3,
      },
      ...getColumnSearchProps("first_name"),
      render: (text) => <div className="text-base capitalize">{text}</div>,
    },
    // {
    //   title: "Last Name",
    //   dataIndex: "last_name",
    //   width: 150,
    //   fixed: "left",
    //   sorter: {
    //     compare: (a, b) => strcmp(a.last_name, b.last_name),
    //     multiple: 2,
    //   },
    //   ...getColumnSearchProps("last_name"),
    //   render: (text) => <div className="text-base capitalize">{text}</div>,
    // },
    ...Array.from({
      length:
        dateRange[1] && dateRange[0]
          ? moment(dateRange[1]).diff(moment(dateRange[0]), "day") + 1
          : 0,
    }).map((_, index) => {
      const date = moment(dateRange[0]).add(index, "days").format("DD-MM-YYYY");

      return {
        title: date,
        dataIndex: "_id",
        align: "center",
        width: 100,
        render: (id, record) => {
          const dateStr = moment(date, "DD-MM-YYYY").format("YYYY-MM-DD");
          const holidayName = historyData?.data?.holidayMap?.[dateStr];
          const attendanceData = attendanceMap[record._id]?.[date];
          const hasCheckIn = attendanceData?.hasCheckIn;
          const hasCheckOut = attendanceData?.hasCheckOut;
          const hasPermission = attendanceData?.hasPermission;

          if (holidayName) {
            return (
              <div className="flex items-center justify-center">
                <Tag color="blue" title={holidayName} style={{ margin: 0 }}>
                  H
                </Tag>
              </div>
            );
          }

          if (hasCheckIn && hasCheckOut) {
            return (
              <div className="flex items-center justify-center">
                <BiCheckCircle className="text-green-500" size={20} />
              </div>
            );
          } else if (hasCheckIn && !hasCheckOut) {
            return (
              <div className="flex items-center justify-center">
                <BiCheckCircle
                  className="text-orange-500"
                  size={20}
                  title="Partial"
                />
              </div>
            );
          } else if (hasPermission) {
            return (
              <div className="flex items-center justify-center">
                <BiCheckCircle className="text-blue-500" size={20} />
              </div>
            );
          } else if (attendanceData) {
            return (
              <div className="flex items-center justify-center">
                <BiXCircle className="text-red-500" size={20} />
              </div>
            );
          } else {
            return (
              <div className="flex items-center justify-center">
                <BiCircle className="text-gray-400" size={20} />
              </div>
            );
          }
        },
      };
    }),
  ];

  return (
    <div>
      <div className="text-xl font-semibold pb-4">
        Montessori Attendance History
      </div>
      <div className="bg-white p-5 rounded">
        <Form
          form={form}
          layout="vertical"
          name="basic"
          autoComplete="off"
          initialValues={{
            class: propClassId,
          }}
        >
          <div className="pb-2 text-base font-medium">Choose a Range</div>
          <div className="flex items-center justify-between gap-4">
            <RangePicker
              value={dateRange}
              format={"DD/MM/YYYY"}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange(dates);
                }
              }}
              disabledDate={(d) => !d || d.isAfter(moment().endOf("day"))}
            />
            <Button
              type="primary"
              onClick={handleExport}
              disabled={!selectedClassId || !dateRange[0] || !dateRange[1]}
            >
              Export
            </Button>
          </div>
        </Form>

        <div className="mt-4">
          <Table
            // pagination={{ pageSize: 20, position: ["bottomCenter"] }}
            scroll={{ x: "max-content" }}
            className="rounded-2xl"
            columns={columns}
            dataSource={historyData?.data?.students || []}
            loading={historyData?.isLoading}
            rowKey={(record) => record._id}
          />
        </div>
      </div>
    </div>
  );
};

export default AttendanceViewHistory;
